import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Mail, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { userService } from '@/services/storageService';
import aivLogo from '@/assets/aiv-logo-trans.png';
import { useI18n } from '@/contexts/I18nContext';

export default function ForgotPassword() {
  const { t } = useI18n();
  const location = useLocation();
  const prefilledEmail =
    (location.state as { email?: string } | undefined)?.email ||
    '';

  const [email, setEmail] = useState(prefilledEmail);
  const [isSending, setIsSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [lastRequestedEmail, setLastRequestedEmail] = useState('');

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleSendReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error(t('forgot_enter_email_reset', 'Enter your email to receive a reset link'));
      return;
    }
    if (cooldown > 0) return;

    setIsSending(true);
    try {
      await userService.requestPasswordReset(email);
      setLastRequestedEmail(email);
      setCooldown(60);
      toast.success(t('forgot_reset_link_sent', 'If the email exists, a reset link has been sent'));
    } catch {
      toast.error(t('forgot_reset_link_failed', 'Could not send reset link'));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-accent/10 p-4">
      <div className="w-full max-w-md md:max-w-3xl lg:max-w-4xl animate-fade-in">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center rounded-2xl text-primary-foreground mb-4">
            <img style={{height:"130px", width:"130px"}} src={aivLogo} alt="AIV Logo" className="object-contain" />
          </div>
          <h1 className="text-3xl md:text-4xl font-display font-bold text-foreground">{t('forgot_title', 'Reset your password')}</h1>
          <p className="text-muted-foreground mt-2 text-sm md:text-lg">{t('forgot_subtitle', 'We will email you a secure reset link.')}</p>
        </div>

        <Card className="border-border/50 shadow-xl">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl md:text-4xl font-display">{t('forgot_heading', 'Forgot password')}</CardTitle>
            <CardDescription className="text-sm md:text-lg">{t('forgot_description', 'Enter the email associated with your account.')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 md:space-y-6">
            {lastRequestedEmail && (
              <Alert className="border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-500/40 dark:bg-blue-900/20">
                <AlertTitle className="flex items-center gap-2">
                  <Mail size={18} /> {t('forgot_check_inbox', 'Check your inbox')}
                </AlertTitle>
                <AlertDescription>
                  We sent a reset link to {lastRequestedEmail}. The link expires shortly, so use it soon.
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSendReset} className="space-y-4 md:space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-base md:text-lg">{t('login_email', 'Email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11 md:h-14 text-base md:text-lg"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-11 md:h-14 text-base md:text-lg"
                disabled={isSending || cooldown > 0}
              >
                {cooldown > 0
                  ? `${t('forgot_resend_in', 'Resend in')} ${cooldown}s`
                  : isSending
                    ? t('forgot_sending_link', 'Sending link...')
                    : t('forgot_send_link', 'Send reset link')}
              </Button>
            </form>

            <div className="flex items-center justify-between text-sm pt-2">
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-primary hover:underline"
              >
                <ArrowLeft size={16} /> {t('forgot_back_login', 'Back to login')}
              </Link>
              <span className="text-muted-foreground text-xs">{t('forgot_need_help', 'Need help? Contact your club admin.')}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
